import type { ComponentProps, ComponentType } from "react";
import {
  LloydsBankLogo,
  NatWestLogo,
  UnlikelyAILogo,
} from "@targeted-support/ui/components/icons";
import { config } from "../config";

export type BrandingColors = {
  primary: string;
  primaryHover: string;
  secondary: string;
  accent: string;
  background: string;
  foreground: string;
  muted: string;
  border: string;
  card: string;
};

/**
 * Props for logo components used in branding
 */
export interface LogoProps extends ComponentProps<"svg"> {
  size?: number;
}

export interface OrganizationBranding {
  /**
   * Auth0 organization name (id token claim: org_name)
   */
  tenantId: string;
  /**
   * What to call the customer on screen, e.g. "NatWest". The Overview page names
   * them against UnlikelyAI, so a tenant without a configured name falls back to
   * a generic one rather than reading as UnlikelyAI's own product.
   */
  name: string;
  /**
   * Optional logo component to render in-app.
   * Accepts standard SVG props plus a size prop (in rem).
   */
  logo?: ComponentType<LogoProps>;
  colors: BrandingColors;
}

export const defaultBranding: Omit<OrganizationBranding, "tenantId"> = {
  name: "The bank",
  colors: {
    primary: "#B11082",
    primaryHover: "#E014A4",
    secondary: "#FFFFFF",
    accent: "#ED3AB9",
    background: "#FBFBFB",
    foreground: "#0F172A",
    muted: "#616D7D",
    border: "#D5DCE6",
    card: "#FFFFFF",
  },
  logo: UnlikelyAILogo,
};

/**
 * Map Auth0 `org_id` → branding config.
 *
 * Keys must match the `org_id` claim exactly.
 */
export const brandingByTenantId: Record<
  string,
  Omit<OrganizationBranding, "tenantId">
> = {
  [config.LLOYDS_ORGANISATION_ID]: {
    name: "Lloyds Bank",
    colors: {
      primary: "#00864F",
      primaryHover: "#006B3F",
      secondary: "#6E6B6F",
      accent: "#11B67A",
      background: "#FFFFFF",
      foreground: "#0F172A",
      muted: "#616D7D",
      border: "#D5DCE6",
      card: "#FFFFFF",
    },
    logo: LloydsBankLogo,
  },
  [config.NATWEST_ORGANISATION_ID]: {
    name: "NatWest",
    colors: {
      primary: "#3C1053",
      primaryHover: "#42145F",
      secondary: "#1E1E1E",
      accent: "#5C0F8B",
      background: "#FAFAFA",
      foreground: "#1E1E1E",
      muted: "#6B7280",
      border: "#E5E7EB",
      card: "#FFFFFF",
    },
    logo: NatWestLogo,
  },
};
